import type { DashboardLibraryState } from '@highimpact/dashboard-types';
import { initialDashboardLibraryState } from '@highimpact/dashboard-types';
import {
  configureStore,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  Provider,
  createDispatchHook,
  createSelectorHook,
  type ReactReduxContextValue,
} from 'react-redux';

import type { DashboardHostApi } from '../api/DashboardHostApi';
import type {
  DashboardHttpTransport,
  DashboardLibraryThunkExtra,
} from '../api/httpTransport';
import { dashboardLibraryReducer } from '../state/rootReducer';
import { WidgetInstanceRegistryProvider } from './instanceRegistry/WidgetInstanceRegistryProvider';

/** Isolated react-redux context so the library store does not shadow the host store. */
export const DashboardLibraryReduxContext =
  createContext<ReactReduxContextValue<
    DashboardLibraryState,
    UnknownAction
  > | null>(null);

const useLibraryDispatch = createDispatchHook(DashboardLibraryReduxContext);
const useLibrarySelector = createSelectorHook(DashboardLibraryReduxContext);

export type DashboardLibraryStore = ReturnType<
  typeof createDashboardLibraryStore
> & {
  readonly libThunkExtra: DashboardLibraryThunkExtra;
};
export type DashboardLibraryDispatch = ThunkDispatch<
  DashboardLibraryState,
  DashboardLibraryThunkExtra,
  UnknownAction
>;

const DashboardLibraryStoreContext =
  createContext<DashboardLibraryStore | null>(null);

const defaultTransport: DashboardHttpTransport = {
  config: {
    get: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
    post: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
  } as unknown as DashboardHttpTransport['config'],
  dataDashboard: {
    get: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
    post: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
  } as unknown as DashboardHttpTransport['dataDashboard'],
  permissions: {
    get: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
    post: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
  } as unknown as DashboardHttpTransport['permissions'],
  semantic: {
    get: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
    post: () =>
      Promise.reject(new Error('Dashboard HTTP transport not configured')),
  } as unknown as DashboardHttpTransport['semantic'],
  getAuthToken: () => null,
};

export interface CreateDashboardLibraryStoreOptions {
  readonly preloadedState?: DashboardLibraryState;
  /** @deprecated Prefer {@link DashboardHostApi.measures} and query hooks. */
  readonly transport?: DashboardHttpTransport;
  readonly hostApi: DashboardHostApi;
}

export function createDashboardLibraryStore(
  options: CreateDashboardLibraryStoreOptions
) {
  const extra: DashboardLibraryThunkExtra = {
    transport: options.transport ?? defaultTransport,
    api: options.hostApi,
  };

  const store = configureStore({
    reducer: dashboardLibraryReducer,
    preloadedState: options.preloadedState ?? initialDashboardLibraryState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        thunk: { extraArgument: extra },
      }),
  });

  return Object.assign(store, { libThunkExtra: extra });
}

export interface DashboardLibraryProviderProps {
  readonly children: ReactNode;
  readonly hostApi: DashboardHostApi;
  /** For tests/storybook only - not from host Redux. */
  readonly preloadedState?: DashboardLibraryState;
  /** @deprecated Prefer {@link DashboardHostApi.measures}. */
  readonly transport?: DashboardHttpTransport;
  /** Optional embed override; defaults to the nearest app-level QueryClientProvider. */
  readonly queryClient?: QueryClient;
}

export function DashboardLibraryProvider({
  children,
  hostApi,
  preloadedState,
  transport,
  queryClient,
}: DashboardLibraryProviderProps) {
  const storeRef = useRef<DashboardLibraryStore | null>(null);

  if (!storeRef.current) {
    storeRef.current = createDashboardLibraryStore({
      preloadedState,
      transport,
      hostApi,
    });
  }

  const tree = (
    <Provider store={storeRef.current} context={DashboardLibraryReduxContext}>
      <DashboardLibraryStoreContext.Provider value={storeRef.current}>
        <WidgetInstanceRegistryProvider>
          {children}
        </WidgetInstanceRegistryProvider>
      </DashboardLibraryStoreContext.Provider>
    </Provider>
  );

  if (queryClient) {
    return (
      <QueryClientProvider client={queryClient}>{tree}</QueryClientProvider>
    );
  }

  return tree;
}

export function useDashboardLibraryStore(): DashboardLibraryStore {
  const store = useContext(DashboardLibraryStoreContext);
  if (!store) {
    throw new Error(
      'useDashboardLibraryStore must be used within DashboardLibraryProvider'
    );
  }
  return store;
}

export function useDashboardLibrarySelector<TSelected>(
  selector: (state: DashboardLibraryState) => TSelected
): TSelected {
  return useLibrarySelector(selector);
}

export function useDashboardLibraryDispatch(): DashboardLibraryDispatch {
  return useLibraryDispatch();
}

/** Re-export for runtime helpers that mirror the dashboard slice only. */
export function useDashboardLibraryDashboardState(): DashboardLibraryState['dashboard'] {
  return useDashboardLibrarySelector((state) => state.dashboard);
}

export function useDashboardLibraryDashboardDispatch(): DashboardLibraryDispatch {
  return useDashboardLibraryDispatch();
}

export function useOptionalDashboardLibraryStore():
  | DashboardLibraryStore
  | undefined {
  return useContext(DashboardLibraryStoreContext) ?? undefined;
}

export function useMemoizedDashboardLibrarySelector<TSelected>(
  selector: (state: DashboardLibraryState) => TSelected,
  deps: readonly unknown[]
): TSelected {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoSelector = useMemo(() => selector, deps);
  return useDashboardLibrarySelector(memoSelector);
}

export type { DashboardHttpTransport, DashboardLibraryThunkExtra };
